import React, { FC, useMemo } from 'react';
import { getText } from '../../../../utilities/CultureUtilities';
import { Product } from '../../../../models/form/product';
import { InputGroup, Table } from 'react-bootstrap';
import NumberInput from '../number/NumberInput';
import _ from 'lodash';
import { useIntl } from 'react-intl';

export interface Props {
  value: Array<{ id: string; quantity: number }>;
  products: Product[];
  onChange: (value: Array<{ id: string; quantity: number }>) => void;
  readOnlyMode?: boolean;
  errorComponent?: (id: string) => JSX.Element;
  culture?: string;
  fieldId: string;
  fieldProperties: any;
}

const Products: FC<Props> = ({
  value,
  products,
  fieldProperties,
  fieldId,
  onChange,
  culture,
  readOnlyMode,
  errorComponent,
}: Props) => {
  const intl = useIntl();
  const limitsReached = useMemo(() => {
    const limitsReached = {};

    for (const product of products) {
      let limitReached = false;
      if (product.limit && fieldProperties) {
        const claimedProperty =
          _.get(
            fieldProperties.properties.find(
              (p) => p.id == fieldId && p.properties.productId == product.value && p.type == 'claimed',
            ),
            'properties.quantity',
          ) || 0;

        limitReached = _.parseInt(claimedProperty) >= product.limit;
      }

      limitsReached[product.value] = limitReached;
    }

    return limitsReached;
  }, [products, fieldProperties]);

  return (
    <div className="products">
      <Table responsive>
        <thead>
          <tr>
            <td>{intl.formatMessage({ id: 'library.productsField.product' })}</td>
            <td className="text-center" style={{ paddingRight: '53px' }}>
              {intl.formatMessage({ id: 'library.productsField.quantity' })}
            </td>
            <td className="text-center">{intl.formatMessage({ id: 'library.productsField.price' })}</td>
            <td className="text-center">{intl.formatMessage({ id: 'library.productsField.subtotal' })}</td>
          </tr>
        </thead>
        <tbody>
          {products?.map((product) => {
            const quantity = _.find(value, { id: product.value });
            let subtotal = product.price * (quantity?.quantity || 0);
            if (subtotal < 0) {
              subtotal = 0;
            }

            return (
              <React.Fragment key={product.value}>
                <tr>
                  <td>
                    <div className="name">{getText(product.text, culture)}</div>
                    {limitsReached[product.value] && (
                      <div className="feedback-invalid" data-testid="feedback-invalid">
                        {intl.formatMessage(
                          { id: 'fields.fieldErrors.limit' },
                          { quantityLeft: 0, product: getText(product.text, intl.locale) },
                        )}
                      </div>
                    )}
                    {!limitsReached[product.value] && errorComponent && errorComponent(product.value)}
                  </td>
                  <td className="quantity">
                    <div className="d-flex align-items-center">
                      <InputGroup>
                        <NumberInput
                          value={quantity?.quantity}
                          enabled={!readOnlyMode && !limitsReached[product.value]}
                          onChange={(e) => {
                            if (!readOnlyMode) {
                              const quantityInput = parseInt(e.target.value);
                              onChange([
                                ...value.filter((item) => item.id != product.value),
                                ...(!isNaN(quantityInput) ? [{ id: product.value, quantity: quantityInput }] : []),
                              ]);
                            }
                          }}
                        ></NumberInput>
                      </InputGroup>
                      <div style={{ width: '35px' }}>
                        {(quantity?.quantity || 0) > 0 && (
                          <span
                            style={readOnlyMode ? { cursor: 'default', color: 'rgba(0, 0, 0, 0.5)' } : {}}
                            onClick={() => {
                              if (!readOnlyMode) {
                                onChange([
                                  ...value.filter((item) => item.id != product.value),
                                  { id: product.value, quantity: 0 },
                                ]);
                              }
                            }}
                            className="mdi mdi-close ms-1"
                          ></span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="price text-center text-nowrap">
                    {(Number(product.price) / 100).toFixed(2) || '0.00'} $
                  </td>
                  <td className="subtotal text-center text-nowrap">
                    {(Number(subtotal) / 100).toFixed(2) || '0.00'} $
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default Products;
